import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { faChevronDown, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { Link } from "react-router-dom";
// import { useAuth0 } from "@auth0/auth0-react";
// import { useViewPermission } from "../context/PermissionContext";
import HeroArt from "./HeroArt";

function MainImageHeader(props) {
  // Previous Register CTA dependencies:
  // const { loginWithRedirect, isAuthenticated } = useAuth0();
  // const { hasPermission, loading: permLoading } = useViewPermission(
  //   "view_register_courses",
  // );
  const [mountNode, setMountNode] = useState(null);
  const parallaxRaf = useRef(0);

  useEffect(() => {
    setMountNode(document.body);
  }, []);

  // Cursor parallax: image drifts with the pointer, copy drifts gently against it.
  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
    if (reduce || coarse) return undefined;

    const root = document.documentElement;
    let targetX = 0;
    let targetY = 0;

    const apply = () => {
      parallaxRaf.current = 0;
      root.style.setProperty("--hero-mx", `${targetX.toFixed(2)}px`);
      root.style.setProperty("--hero-my", `${targetY.toFixed(2)}px`);
    };

    const onMove = (e) => {
      if (!document.body.classList.contains("student-landing-mode-hero")) return;
      const nx = e.clientX / window.innerWidth - 0.5; // -0.5 .. 0.5
      const ny = e.clientY / window.innerHeight - 0.5;
      targetX = nx * 20;
      targetY = ny * 14;
      if (!parallaxRaf.current) {
        parallaxRaf.current = window.requestAnimationFrame(apply);
      }
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
      if (!parallaxRaf.current) {
        parallaxRaf.current = window.requestAnimationFrame(apply);
      }
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);

    return () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
      if (parallaxRaf.current) window.cancelAnimationFrame(parallaxRaf.current);
      root.style.removeProperty("--hero-mx");
      root.style.removeProperty("--hero-my");
    };
  }, []);

  // Ambient pastel particles — deterministic layout so SSR/CSR match
  const particles = [
    { x: 8, y: 78, s: 7, d: 0, t: 19, c: "mint" },
    { x: 16, y: 30, s: 4, d: 3.2, t: 23, c: "lavender" },
    { x: 24, y: 62, s: 5, d: 6.1, t: 21, c: "peach" },
    { x: 33, y: 88, s: 3, d: 1.4, t: 17, c: "mint" },
    { x: 42, y: 18, s: 6, d: 8.3, t: 25, c: "lavender" },
    { x: 55, y: 72, s: 4, d: 4.6, t: 20, c: "pink" },
    { x: 63, y: 40, s: 8, d: 10.2, t: 27, c: "mint" },
    { x: 71, y: 84, s: 3, d: 2.7, t: 18, c: "peach" },
    { x: 79, y: 26, s: 5, d: 7.5, t: 22, c: "lavender" },
    { x: 88, y: 66, s: 6, d: 5.1, t: 24, c: "mint" },
    { x: 94, y: 48, s: 4, d: 9.4, t: 21, c: "pink" },
    { x: 48, y: 94, s: 5, d: 11.8, t: 26, c: "lavender" },
  ];

  // Previous Register CTA (Auth0 login / /registercourse by permission)
  // const showRegisterCta =
  //   !isAuthenticated || (!permLoading && hasPermission);

  const scrollToContent = () => {
    if (typeof props.onExplore === "function") {
      props.onExplore();
      return;
    }
    const body = document.querySelector(".student-landing__body");
    if (body) {
      body.scrollIntoView({ behavior: "auto", block: "start" });
    } else {
      window.scrollTo({ top: window.innerHeight, behavior: "auto" });
    }
  };

  const hero = (
    <header className="student-hero" aria-label="Zainematics virtual classes">
      <div className="student-hero__media" aria-hidden="true">
        <HeroArt />
      </div>
      <div className="student-hero__overlay" aria-hidden="true" />
      <div className="student-hero__particles" aria-hidden="true">
        {particles.map((p, i) => (
          <span
            key={i}
            className={`student-hero__particle student-hero__particle--${p.c}`}
            style={{
              "--px": `${p.x}%`,
              "--py": `${p.y}%`,
              "--ps": `${p.s}px`,
              "--pd": `${p.d}s`,
              "--pt": `${p.t}s`,
            }}
          />
        ))}
      </div>
      <div className="student-hero__content">
        <p className="student-hero__brand">ZAINEMATICS</p>
        <h1 className="student-hero__headline">VIRTUAL CLASSES</h1>
        <p className="student-hero__tagline">PAVE YOUR WAY TO SUCCESS</p>
        {/* Previous Register behavior:
        {showRegisterCta &&
          (props.loggedIn ? (
            <Link to="/registercourse" className="student-hero__cta">
              REGISTER
              <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
            </Link>
          ) : (
            <button
              type="button"
              className="student-hero__cta"
              onClick={() => loginWithRedirect()}
            >
              REGISTER
              <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
            </button>
          ))}
        */}
        <button
          type="button"
          className="student-hero__cta"
          onClick={() =>
            window.open(
              "https://form.typeform.com/to/eqfTsnHg?typeform-source=www.zainematics.com",
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          REGISTER
          <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
        </button>
      </div>
      <button
        type="button"
        className="student-hero__scroll"
        onClick={scrollToContent}
        aria-label="Scroll to explore more"
      >
        <span className="student-hero__scroll-label">Explore</span>
        <span className="student-hero__scroll-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </button>
    </header>
  );

  return (
    <>
      {/* Spacer keeps page scroll length; hero is portaled to <body> for true viewport cover */}
      <div className="student-hero-stage" aria-hidden="true" />
      {mountNode ? createPortal(hero, mountNode) : hero}
    </>
  );
}

export default MainImageHeader;
